import {
  ModButton,
  ModModal,
  ModModalContent,
  ModModalFooter,
  ModModalHeader
} from '@moderneinc/design-system-components';
import {
  semanticColors,
  spacing,
  typography
} from '@moderneinc/design-system-tokens';
import { Box, List, ListItem, Typography } from '@mui/material';
import { dialogClasses } from '@mui/material/Dialog';
import { dialogContentClasses } from '@mui/material/DialogContent';
import Image from 'next/image';
import { type FunctionComponent, useEffect, useState } from 'react';
import { safeLocalStorage } from '../../helpers/local-storage.helper';
import { hasSaasV1State } from '../../helpers/saas-v1.helper';
import { useBetaSeen } from '../../hooks/use-beta-seen.hooks';
import { useTourEngineStore } from '../../stores/tour-engine.store';
import { DAY_ZERO_TOUR_ID } from '../guided-tour/day-zero-tour.config';

/** Show-once key in the beta-seen store. */
export const ANNOUNCEMENT_SEEN_KEY = 'onboarding-v2-announcement';

/** Pre-dates the move into the beta-seen store; read once to carry dismissals. */
const LEGACY_STORAGE_KEY = 'moderne-onboarding-v2-dismissed';

/** Beat before the announcement appears, so it doesn't slam up on load. */
export const ANNOUNCEMENT_DELAY_MS = 500;

const hasLegacyDismissal = (): boolean =>
  safeLocalStorage.getItem(LEGACY_STORAGE_KEY) === 'true';

// Fixed modal height (per Figma) so it never shifts between slides.
const MODAL_HEIGHT = 538;

type Feature = {
  title: string;
  description: string;
};

type Slide = {
  image: string;
  intro?: string;
  header?: string;
  features: Feature[];
};

const SLIDES: Slide[] = [
  {
    image: '/saas-announcement-scene-1.png',
    intro:
      'We’ve upgraded the Moderne Platform with a new foundation designed for speed, scale, and clarity.',
    header: 'What’s new:',
    features: [
      {
        title: 'Real-time visibility into platform health',
        description: 'Track recipe runs and infrastructure status at a glance.'
      },
      {
        title: 'Your results stick around',
        description:
          'Recipe results persist across deploys—revisit past runs or pick up where you left off.'
      }
    ]
  },
  {
    image: '/saas-announcement-scene-2.png',
    features: [
      {
        title: 'Changelog: Unified view of commits and PRs',
        description:
          'Browse every recent commit and pull request across your organization in one place.'
      },
      {
        title: 'Improved UI',
        description:
          'A refreshed organization navigator, a more robust marketplace, and polish throughout.'
      }
    ]
  }
];

type Props = {
  initialSlide?: number;
};

export const OnboardingModal: FunctionComponent<Props> = ({
  initialSlide = 0
}) => {
  const [open, setOpen] = useState(false);
  const [activeSlide, setActiveSlide] = useState(initialSlide);
  const start = useTourEngineStore((state) => state.start);
  const { isSeen, markSeen } = useBetaSeen(ANNOUNCEMENT_SEEN_KEY);

  useEffect(() => {
    if (isSeen) {
      return undefined;
    }
    // Carry over a dismissal made before this moved into the beta-seen store.
    if (hasLegacyDismissal()) {
      markSeen();
      return undefined;
    }
    // Returning users only — a first-time visitor has no migration to announce,
    // and TourEngineController offers them the Day Zero welcome instead (#8715).
    if (!hasSaasV1State()) {
      return undefined;
    }
    const timer = setTimeout(() => setOpen(true), ANNOUNCEMENT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isSeen, markSeen]);

  const handleClose = () => {
    markSeen();
    setOpen(false);
  };

  const isLastSlide = activeSlide === SLIDES.length - 1;

  const handleStartTour = () => {
    markSeen();
    setOpen(false);
    start(DAY_ZERO_TOUR_ID);
  };

  return (
    <ModModal
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={{
        [`& .${dialogClasses.paper}`]: { height: MODAL_HEIGHT },
        [`& .${dialogClasses.paper}:focus`]: { outline: 'none' },
        [`& .${dialogContentClasses.root}`]: {
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <ModModalHeader title="SaaS v2 is here" onClose={handleClose} />
      <ModModalContent>
        <Box
          sx={{
            position: 'relative',
            flex: '1 1 auto',
            minHeight: 0,
            mb: `${spacing.spacing_3}px`,
            overflow: 'hidden'
          }}
        >
          {SLIDES.map((s, index) => (
            <Image
              key={s.image}
              src={s.image}
              alt=""
              fill
              priority={index === 0}
              style={{
                objectFit: 'cover',
                objectPosition: 'center center',
                opacity: index === activeSlide ? 1 : 0,
                transition: 'opacity 0.3s ease'
              }}
            />
          ))}
        </Box>
        <Box sx={{ display: 'grid', flexShrink: 0 }}>
          {SLIDES.map((s, index) => (
            <Box
              key={s.image}
              sx={{
                gridArea: '1 / 1',
                opacity: index === activeSlide ? 1 : 0,
                pointerEvents: index === activeSlide ? 'auto' : 'none',
                transition: 'opacity 0.3s ease'
              }}
            >
              {s.intro !== undefined && (
                <Typography
                  variant="body"
                  sx={{
                    color: semanticColors.color.text.secondary,
                    display: 'block',
                    mb: `${spacing.spacing_3}px`
                  }}
                >
                  {s.intro}
                </Typography>
              )}

              {s.header !== undefined && (
                <Typography
                  sx={{ fontWeight: 'bold', mb: `${spacing.spacing_1_2}px` }}
                >
                  {s.header}
                </Typography>
              )}

              <List
                sx={{
                  listStyleType: 'disc',
                  pl: `${spacing.spacing_3}px`,
                  py: 0
                }}
              >
                {s.features.map((feature) => (
                  <ListItem
                    key={feature.title}
                    sx={{
                      display: 'list-item',
                      px: `${spacing.spacing_1_2}px`,
                      py: `${spacing.spacing_3_4}px`
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 'bold',
                        fontSize: typography.fontSize.sm
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body"
                      sx={{
                        color: semanticColors.color.text.inputHelper,
                        fontSize: typography.fontSize.sm
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </Box>
          ))}
        </Box>
      </ModModalContent>

      <ModModalFooter
        leadingContent={
          <ModButton variant="tertiary" size="medium" onClick={handleClose}>
            Dismiss
          </ModButton>
        }
      >
        <ModButton
          variant="tertiary"
          size="medium"
          onClick={() => setActiveSlide((prev) => prev - 1)}
          disabled={activeSlide === 0}
        >
          Back
        </ModButton>
        <ModButton
          variant="primary"
          size="medium"
          onClick={
            isLastSlide
              ? handleStartTour
              : () => setActiveSlide((prev) => prev + 1)
          }
        >
          {isLastSlide ? 'Take the product tour' : 'Next'}
        </ModButton>
      </ModModalFooter>
    </ModModal>
  );
};
